// src/pages/MyCompanyPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../DataContext";
import { apiRequest } from "../api/http";
import { useTranslation } from "react-i18next";

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

type CompanyInfo = {
  tenantId: string;
  legalName: string;
  dbaName: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  taxId: string;
  licenseNo: string;
  notes: string;
  logoUrl: string;
  createdAt: string | null;
  updatedAt: string | null;
};

function digitsOnly(s: string) {
  return (s || "").replace(/\D+/g, "");
}

// Formats to: +1 (NNN) NNN-NNNN (US)
// Accepts 10 digits, or 11 digits starting with 1.
// Formats to: +1 (NNN) NNN-NNNN (US)
// Accepts user typing; prevents the "+1" we inject from re-injecting digits.
function formatUsPhone(input: string) {
  let digits = digitsOnly(input);

  // Because our formatted string includes "+1", digitsOnly() will always pick up that "1".
  // Strip a leading "1" every time (NANP numbers never have area codes starting with 0/1 anyway).
  if (digits.startsWith("1")) digits = digits.slice(1);

  // Limit to 10 digits
  digits = digits.slice(0, 10);

  if (digits.length === 0) return "";

  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);

  if (digits.length <= 3) return `+1 (${a}`;
  if (digits.length <= 6) return `+1 (${a}) ${b}`;
  return `+1 (${a}) ${b}-${c}`;
}

export default function MyCompanyPage() {
  const { t } = useTranslation();
  const data = useData() as any;
  const tenantId: string = (
    data?.activeTenantId ||
    data?.tenantId ||
    "demo"
  ).toString();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [base, setBase] = useState<CompanyInfo | null>(null);
  const [draft, setDraft] = useState<CompanyInfo | null>(null);

  const dirty = useMemo(() => {
    if (!base || !draft) return false;
    return JSON.stringify(base) !== JSON.stringify(draft);
  }, [base, draft]);

  const legalNameOk = useMemo(() => {
    const v = (draft?.legalName || "").trim();
    return v.length > 0;
  }, [draft?.legalName]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setErr(null);
      try {
        const res = await apiRequest<ApiOk<CompanyInfo> | ApiErr>(
          "GET",
          "/api/company",
          { tenantId }
        );
        if (!(res as any).ok)
          throw new Error((res as any).error || "Failed to load company info");

        setBase((res as any).data);
        setDraft((res as any).data);
      } catch (e: any) {
        setErr(e?.message || "Failed to load company info");
        setBase(null);
        setDraft(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [tenantId]);

  function setField<K extends keyof CompanyInfo>(key: K, value: CompanyInfo[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  async function onSave() {
    if (!draft) return;

    const legal = (draft.legalName || "").trim();
    if (!legal) {
      setErr(t("myCompany.legalNameRequired"));
      return;
    }

    setSaving(true);
    setErr(null);

    try {
      // Only send editable fields
      const body = {
        legalName: legal,
        dbaName: (draft.dbaName || "").trim(),
        address: (draft.address || "").trim(),
        phone: digitsOnly(draft.phone || "").slice(-10),
        email: (draft.email || "").trim(),
        website: (draft.website || "").trim(),
        taxId: (draft.taxId || "").trim(),
        licenseNo: (draft.licenseNo || "").trim(),
        notes: (draft.notes || "").trim(),
        logoUrl: (draft.logoUrl || "").trim(),
      };

      const res = await apiRequest<ApiOk<CompanyInfo> | ApiErr>(
        "PUT",
        "/api/company",
        { tenantId, body }
      );

      if (!(res as any).ok)
        throw new Error((res as any).error || "Failed to save company info");

      setBase((res as any).data);
      setDraft((res as any).data);
    } catch (e: any) {
      setErr(e?.message || "Failed to save company info");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>My Company Info</h1>
          <div className="pageNote">{t("myCompany.pageNote")}</div>
        </div>

        <button
          className="btn btnSm"
          type="button"
          onClick={onSave}
          disabled={loading || saving || !dirty || !draft || !legalNameOk}
          title={
            !draft
              ? t("myCompany.noData")
              : !legalNameOk
              ? t("myCompany.legalRequired")
              : !dirty
              ? t("myCompany.noChanges")
              : t("myCompany.saveChanges")
          }
        >
          {saving ? t("myCompany.savingBtn") : t("myCompany.saveBtn")}
        </button>
      </div>

      {err ? (
        <div className="card cardTight" style={{ marginTop: 14 }}>
          <div className="formError" role="alert">
            {err}
          </div>
        </div>
      ) : null}

      {loading || !draft ? (
        <div className="card cardTight" style={{ marginTop: 14 }}>
          <div className="pageNote">{loading ? t("myCompany.loadingMsg") : t("myCompany.noDataMsg")}</div>
        </div>
      ) : (
        <div className="card cardTight" style={{ marginTop: 14 }}>
          <div className="cardHeader">
            <h3 className="cardTitle">{t("myCompany.companyDetails")}</h3>
            <div className="cardSub">{dirty ? t("myCompany.unsavedChanges") : t("myCompany.upToDate")}</div>
          </div>

          {/* Desktop: 3 columns, Mobile: 1 column */}
          <div
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: "2fr 1fr 1fr",
              alignItems: "start",
            }}
          >
            {/* Row 1 */}
            <div className="field" style={{ gridColumn: "1 / span 1" }}>
              <label className="label">{t("myCompany.legalName")} *</label>
              <input
                className="input"
                value={draft.legalName || ""}
                onChange={(e) => setField("legalName", e.target.value)}
                placeholder={t("myCompany.legalNamePlaceholder")}
              />
              <div className="pageNote" style={{ marginTop: 6 }}>
                {t("myCompany.legalNameNote")}
              </div>
            </div>

            <div className="field">
              <label className="label">{t("myCompany.dbaName")}</label>
              <input
                className="input"
                value={draft.dbaName || ""}
                onChange={(e) => setField("dbaName", e.target.value)}
                placeholder={t("myCompany.dbaPlaceholder")}
              />
            </div>

            <div className="field">
              <label className="label">{t("myCompany.logoUrl")}</label>
              <input
                className="input"
                value={draft.logoUrl || ""}
                onChange={(e) => setField("logoUrl", e.target.value)}
                placeholder={t("myCompany.logoPlaceholder")}
              />
            </div>

            {/* Row 2 */}
            <div className="field" style={{ gridColumn: "1 / span 1" }}>
              <label className="label">{t("common.address")}</label>
              <input
                className="input"
                value={draft.address || ""}
                onChange={(e) => setField("address", e.target.value)}
                placeholder={t("myCompany.addressPlaceholder")}
              />
            </div>

            <div className="field">
              <label className="label">{t("common.phone")}</label>
              <input
                className="input"
                value={draft.phone || ""}
                onChange={(e) => {
                  const next = formatUsPhone(e.target.value);
                  setField("phone", next);
                }}
                placeholder={t("myCompany.phonePlaceholder")}
                inputMode="tel"
                autoComplete="tel"
              />
            </div>

            <div className="field">
              <label className="label">{t("common.email")}</label>
              <input
                className="input"
                value={draft.email || ""}
                onChange={(e) => setField("email", e.target.value)}
                placeholder={t("myCompany.emailPlaceholder")}
                inputMode="email"
                autoComplete="email"
              />
            </div>

            {/* Row 3 */}
            <div className="field">
              <label className="label">{t("myCompany.website")}</label>
              <input
                className="input"
                value={draft.website || ""}
                onChange={(e) => setField("website", e.target.value)}
                placeholder={t("myCompany.websitePlaceholder")}
                inputMode="url"
                autoComplete="url"
              />
            </div>

            <div className="field">
              <label className="label">{t("myCompany.licenseNo")}</label>
              <input
                className="input"
                value={draft.licenseNo || ""}
                onChange={(e) => setField("licenseNo", e.target.value)}
                placeholder={t("myCompany.licensePlaceholder")}
              />
            </div>

            <div className="field">
              <label className="label">{t("myCompany.taxId")}</label>
              <input
                className="input"
                value={draft.taxId || ""}
                onChange={(e) => setField("taxId", e.target.value)}
                placeholder={t("myCompany.taxIdPlaceholder")}
              />
            </div>

            {/* Notes full width */}
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label className="label">{t("common.notes")}</label>
              <textarea
                className="input"
                rows={4}
                value={draft.notes || ""}
                onChange={(e) => setField("notes", e.target.value)}
                style={{ resize: "vertical" }}
                placeholder={t("myCompany.notesPlaceholder")}
              />
            </div>
          </div>

          <div className="pageNote" style={{ marginTop: 10 }}>
            {t("myCompany.footerNote")}
          </div>

          {/* CSS hint: only needed if your global CSS doesn't already handle small screens */}
          <div style={{ marginTop: 10 }}>
            <style>
              {`
                @media (max-width: 860px) {
                  .container .card.cardTight > div[style*="grid-template-columns: 2fr 1fr 1fr"] {
                    grid-template-columns: 1fr !important;
                  }
                  .container .card.cardTight > div[style*="grid-template-columns: 2fr 1fr 1fr"] > div {
                    grid-column: 1 / -1 !important;
                  }
                }
              `}
            </style>
          </div>
        </div>
      )}
    </div>
  );
}

